import { useCallback, useState } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import {
  CHUNK_SIZE,
  completeUpload,
  convertMediaBlobToFile,
  getPresignedUrls,
  initiateUpload,
  uploadFileParts,
} from '@repo/videa-notes';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import configurations from '@/config';
import { endpoints } from '@/react-query/endpoints';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';

import { type NoteGenerationErrorProps, VideaNotesUIState } from '../types';

interface UseAudioUploadProps {
  mediaBlobUrl: string | undefined;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  currentOrganizationId: string;
  currentPracticeId: string;
  patientConsentRecordId: string | undefined;
  selectedPrompt: string | undefined;
  selectedTemplate: string | undefined;
  enableVoiceNotesConsent: boolean;
  enableVoiceNotesCustomTemplates: boolean;
  refetchClinicalNotes: () => Promise<{ data: ClinicalNote[] | undefined }>;
  refetchStandalonePatientVisitNotes: () => Promise<{
    data: ClinicalNote[] | undefined;
  }>;
  onSetUiStatus: (status: VideaNotesUIState) => void;
  onSetIsSheetOpen: (isOpen: boolean) => void;
  onSetSelectedNoteId: (noteId: string | undefined) => void;
  onGenerateSuccess: (generationTime: number) => void;
  onUploadError: () => void;
  onUploadSuccess: (s3UploadTime: number) => void;
}

interface UseAudioUploadReturn {
  processingError: NoteGenerationErrorProps | undefined;
  uploadAudio: (overrideBlobUrl?: string) => Promise<void>;
}

export const useAudioUpload = ({
  mediaBlobUrl,
  unifiedAppointmentId,
  standalonePatientVisitId,
  currentOrganizationId,
  currentPracticeId,
  patientConsentRecordId,
  selectedPrompt,
  selectedTemplate,
  enableVoiceNotesConsent,
  enableVoiceNotesCustomTemplates,
  refetchClinicalNotes,
  refetchStandalonePatientVisitNotes,
  onSetUiStatus,
  onSetIsSheetOpen,
  onSetSelectedNoteId,
  onGenerateSuccess,
  onUploadError,
  onUploadSuccess,
}: UseAudioUploadProps): UseAudioUploadReturn => {
  const queryClient = useQueryClient();
  const { oktaAuth } = useOktaAuth();
  const [processingError, setProcessingError] =
    useState<NoteGenerationErrorProps>();

  const uploadAudio = useCallback(
    async (overrideBlobUrl?: string) => {
      // If overrideBlobUrl is provided (dev file upload), skip consent check
      const isDevUpload = !!overrideBlobUrl;
      const hasRequiredConsent =
        isDevUpload || !enableVoiceNotesConsent || patientConsentRecordId;
      const effectiveBlobUrl = overrideBlobUrl ?? mediaBlobUrl;

      if (effectiveBlobUrl && hasRequiredConsent) {
        try {
          const startTime = new Date();

          /* Start Multipart Upload */
          const audioFile = await convertMediaBlobToFile(
            effectiveBlobUrl,
            unifiedAppointmentId || standalonePatientVisitId,
          );

          // Create upload dependencies with Okta token method
          const uploadDeps = {
            getAccessToken: () => oktaAuth.getAccessToken() || null,
            apiUrl: configurations.API_URL,
          };

          // Start tracking S3 upload time
          const s3UploadStartTime = new Date();

          /**
           * step 1: initiate multipart upload
           */
          const { key, uploadId } = await initiateUpload(
            audioFile.name,
            currentOrganizationId,
            currentPracticeId,
            uploadDeps,
          );

          /**
           * step 2: get presigned URLs
           */
          const partCount = Math.ceil(audioFile.size / CHUNK_SIZE);
          const urls = await getPresignedUrls(
            key,
            uploadId,
            partCount,
            currentOrganizationId,
            currentPracticeId,
            uploadDeps,
          );

          /**
           * step 3: upload each part
           */
          const etags = await uploadFileParts(audioFile, urls);

          // Calculate and track S3 upload time immediately
          const s3UploadTime =
            new Date().getTime() - s3UploadStartTime.getTime();
          onUploadSuccess(s3UploadTime);

          /**
           * step 4: complete upload
           */
          const note = await completeUpload(
            key,
            uploadId,
            etags,
            patientConsentRecordId,
            unifiedAppointmentId,
            standalonePatientVisitId,
            enableVoiceNotesCustomTemplates ? undefined : selectedPrompt,
            currentOrganizationId,
            currentPracticeId,
            uploadDeps,
            enableVoiceNotesCustomTemplates ? selectedTemplate : undefined,
          );

          // Only refetch the relevant notes based on appointment type
          let updatedNotesData: ClinicalNote[] | undefined;
          if (unifiedAppointmentId) {
            const refetchResult = await refetchClinicalNotes();
            updatedNotesData = refetchResult.data;
          }
          if (standalonePatientVisitId) {
            const refetchResult = await refetchStandalonePatientVisitNotes();
            updatedNotesData = refetchResult.data;
          }

          const generationTime = new Date().getTime() - startTime.getTime();
          onGenerateSuccess(generationTime);

          // Invalidate ambient data queries to refresh the table with new note data
          queryClient.invalidateQueries({
            queryKey: [
              endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
            ],
          });
          queryClient.invalidateQueries({
            queryKey: [
              endpoints.ambient.voiceNotes
                .batchNotesByStandalonePatientVisitIds,
            ],
          });

          // Set response - use the updated note from refetched data to ensure we have the latest data
          if (updatedNotesData && updatedNotesData.length > 0) {
            // Find the note that matches the ID from completeUpload
            const updatedNote =
              updatedNotesData.find((n) => n.id === note.id) ||
              updatedNotesData[0];
            onSetSelectedNoteId(updatedNote.id);
          } else {
            // Fallback to the note from completeUpload if refetch didn't work
            onSetSelectedNoteId(note.id);
          }

          onSetUiStatus(VideaNotesUIState.SUMMARY_RESULT);
        } catch (error: unknown) {
          if (!axios.isAxiosError(error)) {
            setProcessingError({
              title: 'An unexpected error occurred.',
              description: 'Please try again later.',
            });
          } else {
            // Axios-specific errors
            const description =
              error.response?.data?.data?.data?.detail ??
              'Please try again later';
            setProcessingError({
              title: 'There was an issue processing your audio recording.',
              description: description,
            });
          }
          onSetUiStatus(VideaNotesUIState.ERROR);
          onUploadError();
        } finally {
          onSetIsSheetOpen(true);
        }

        /* End Multipart Upload */
      } else {
        console.error('mediaBlobUrl is undefined - unable to process audio', {
          effectiveBlobUrl,
          hasRequiredConsent,
        });
      }
    },
    [
      mediaBlobUrl,
      unifiedAppointmentId,
      standalonePatientVisitId,
      currentPracticeId,
      currentOrganizationId,
      patientConsentRecordId,
      selectedPrompt,
      selectedTemplate,
      enableVoiceNotesConsent,
      enableVoiceNotesCustomTemplates,
      queryClient,
      oktaAuth,
      refetchClinicalNotes,
      refetchStandalonePatientVisitNotes,
      onSetUiStatus,
      onSetIsSheetOpen,
      onSetSelectedNoteId,
      onGenerateSuccess,
      onUploadError,
      onUploadSuccess,
    ],
  );

  return {
    processingError,
    uploadAudio,
  };
};
